import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../api';

export function useCreateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => clientApi.createUser(payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'all'] }),
    });
}

export function useFlowUsers() {
    return useQuery({
        queryKey: ['client', 'flow-users'],
        queryFn: async () => {
            const data = await clientApi.fetchFlowUsers();
            return data.users || [];
        },
    });
}

export function useTabPermissions() {
    return useQuery({
        queryKey: ['client', 'tab-permissions'],
        queryFn: async () => {
            const data = await clientApi.fetchTabPermissions();
            return data.permissions || [];
        },
    });
}

export function useTogglePermission() {
    const qc = useQueryClient();
    const queryKey = ['client', 'tab-permissions'];
    return useMutation({
        mutationFn: (payload) => clientApi.toggleTabPermission(payload),
        onMutate: async ({ tab_ids, user_id, active }) => {
            await qc.cancelQueries({ queryKey });
            const previous = qc.getQueryData(queryKey) || [];
            qc.setQueryData(queryKey, (prev = []) => {
                let next = [...prev];
                if (!active) {
                    next = next.filter(p => !(tab_ids.includes(p.tab_id) && p.user_id === user_id));
                } else {
                    const toAdd = tab_ids
                        .filter(tid => !next.some(p => p.tab_id === tid && p.user_id === user_id))
                        .map(tid => ({ tab_id: tid, user_id }));
                    next = [...next, ...toAdd];
                }
                return next;
            });
            return { previous };
        },
        onError: (err, vars, context) => {
            if (context?.previous) qc.setQueryData(queryKey, context.previous);
        },
        onSettled: () => qc.invalidateQueries({ queryKey }),
    });
}

export function useComments(opId) {
    return useQuery({
        queryKey: ['client', 'comments', opId],
        queryFn: async () => {
            const data = await clientApi.fetchComments(opId);
            return data.comments || [];
        },
        enabled: !!opId,
    });
}

export function useAddComment(opId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => clientApi.addComment(opId, payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client', 'comments', opId] }),
    });
}

export function useStages(opId) {
    return useQuery({
        queryKey: ['client', 'stages', opId],
        queryFn: async () => {
            const data = await clientApi.fetchStages(opId);
            return data.stages || [];
        },
        enabled: !!opId,
    });
}

export function useAddStage(opId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => clientApi.addStage(opId, payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client', 'stages', opId] }),
    });
}

// `useUpdateOp`/`useCreateOp` viven en `useClientOps.js` (02.3-client.md paso a) -- ahí sí
// invalidan `['client','ops']`, cosa que esta versión nunca hizo (cada caller compensaba con un
// `fetchOps()` manual).

// Selector explícito de space en AddOpModal (space-default-onboarding.md, decisión #2): lista
// los spaces reales del usuario para permitirle elegir uno distinto al default/activo.
export function useUserSpaces(userId) {
    return useQuery({
        queryKey: ['client', 'user-spaces', userId],
        queryFn: async () => {
            const data = await clientApi.fetchUserSpaces(userId);
            return data.spaces || [];
        },
        enabled: !!userId,
    });
}
